'use strict';

window.dndNextLevel = window.dndNextLevel || {};
dndNextLevel.adventure = dndNextLevel.adventure || {};

const adventure = dndNextLevel.adventure;
adventure.stateModule = dndNextLevel.adventureState;
adventure.recentAdapter = dndNextLevel.adventureRecentAdapter;

adventure.cacheElements = function () {
    adventure.settingSelect = document.getElementById('adventure-setting');
    adventure.settingDescription = document.getElementById('adventure-setting-description');
    adventure.tierSelect = document.getElementById('adventure-tier');
    adventure.hookTableSelect = document.getElementById('adventure-hook-table');
    adventure.encounterObjectiveSelect = document.getElementById('adventure-encounter-objective');
    adventure.restoreForm = document.getElementById('adventure-restore-form');
    adventure.exportLink = document.getElementById('adventure-export-link');
};

adventure.setWorkbenchView = function (view) {
    const layout = document.querySelector('[data-component="controls-result-layout"]');
    if (!layout || !['result', 'customize'].includes(view)) return;

    layout.querySelectorAll('[data-workbench-view]').forEach(panel => {
        panel.classList.toggle('hidden', panel.dataset.workbenchView !== view);
    });
    layout.querySelectorAll('[data-workbench-view-target]').forEach(button => {
        const selected = button.dataset.workbenchViewTarget === view;
        button.setAttribute('aria-pressed', String(selected));
        button.classList.toggle('workbench-action-primary', selected);
        button.classList.toggle('workbench-action-secondary', !selected);
    });
};

adventure.initializeWorkbenchViews = function () {
    document.querySelectorAll('[data-workbench-view-target]').forEach(button => {
        button.addEventListener('click', () => adventure.setWorkbenchView(button.dataset.workbenchViewTarget));
    });
};

adventure.getCurrentAdventureStorageKey = function () {
    return adventure.stateModule.storageKey();
};

adventure.persistCurrentAdventureToLocalStorage = function (saved) {
    return adventure.stateModule.persist(window.localStorage, saved);
};

adventure.readCurrentAdventureFromLocalStorage = function () {
    return adventure.stateModule.read(window.localStorage);
};

adventure.currentRequest = function () {
    return {
        setting: adventure.settingSelect?.value || null,
        tier: adventure.tierSelect?.value || null,
        hookTable: adventure.hookTableSelect?.value || null,
        encounterObjectiveType: adventure.encounterObjectiveSelect?.value || null,
    };
};

adventure.syncCurrentRequest = function () {
    const saved = adventure.readCurrentAdventureFromLocalStorage();
    if (!saved) return false;
    saved.request = adventure.currentRequest();
    if (!adventure.persistCurrentAdventureToLocalStorage(saved)) return false;
    adventure.recentAdapter?.saveCurrent(saved, window.localStorage);
    return true;
};

adventure.renderSelectedSettingDescription = function () {
    if (!adventure.settingSelect || !adventure.settingDescription) return;
    const description = adventure.settingSelect.selectedOptions[0]?.dataset.description || '';
    adventure.settingDescription.textContent = description;
    adventure.settingDescription.classList.toggle('hidden', !description);
};

adventure.formatCurrentAdventure = function (saved) {
    if (!adventure.stateModule.validate(saved)) return '';
    const result = saved.result;
    return [
        result.settingLabel ? `Setting: ${result.settingLabel}` : '',
        result.settingDescription || '',
        `Tier: ${result.tierLabel}`,
        result.situation,
        `Draw In Players (${result.drawInHookTable})`,
        result.drawInHook,
        `Encounter (${result.encounterObjectiveTitle})`,
        result.encounterObjectiveDescription,
        `Climax: ${result.climax}`,
    ].filter(Boolean).join('\n\n');
};

adventure.copyCurrentAdventure = async function () {
    const saved = adventure.readCurrentAdventureFromLocalStorage();
    const contents = adventure.formatCurrentAdventure(saved);
    if (!contents || !navigator.clipboard) return false;
    await navigator.clipboard.writeText(contents);
    adventure.setStatus('Adventure copied.');
    return true;
};

adventure.saveCurrentAdventure = function () {
    const saved = adventure.readCurrentAdventureFromLocalStorage();
    if (!saved || !adventure.persistCurrentAdventureToLocalStorage(saved)) return false;
    adventure.recentAdapter?.saveCurrent(saved, window.localStorage, true);
    adventure.setStatus('Adventure saved and pinned in recent creations.');
    return true;
};

adventure.exportCurrentAdventure = function () {
    const saved = adventure.readCurrentAdventureFromLocalStorage();
    if (!saved || !window.URL || !adventure.exportLink) return false;

    const blobUrl = window.URL.createObjectURL(new Blob(
        [JSON.stringify(saved, null, 2)],
        {type: 'application/json'}
    ));
    const tier = String(saved.result.tierValue).toLowerCase().replaceAll('_', '-');
    adventure.exportLink.href = blobUrl;
    adventure.exportLink.download = `${tier}-adventure.json`;
    adventure.exportLink.click();
    window.URL.revokeObjectURL(blobUrl);
    adventure.setStatus('Adventure exported.');
    return true;
};

adventure.setStatus = function (message) {
    const status = document.getElementById('adventure-status');
    if (status) status.textContent = message;
};

adventure.focusResult = function () {
    adventure.setWorkbenchView('result');
    document.getElementById('adventure-workbench-result')?.focus({preventScroll: true});
};

adventure.handleAfterSwap = function (event) {
    if (event.detail?.target?.id !== 'adventure-workbench-result') return;
    adventure.cacheElements();
    adventure.renderSelectedSettingDescription();
    if (event.detail?.elt?.id !== 'adventure-restore-form') adventure.focusResult();
};

adventure.bindEvents = function () {
    document.addEventListener('change', event => {
        if (event.target?.id === 'adventure-setting') {
            adventure.cacheElements();
            adventure.renderSelectedSettingDescription();
            adventure.syncCurrentRequest();
            return;
        }
        if (['adventure-tier', 'adventure-hook-table', 'adventure-encounter-objective']
            .includes(event.target?.id)) {
            adventure.cacheElements();
            adventure.syncCurrentRequest();
        }
    });

    document.addEventListener('click', event => {
        if (event.target.closest?.('#copy-adventure')) adventure.copyCurrentAdventure();
        if (event.target.closest?.('#save-adventure')) adventure.saveCurrentAdventure();
        if (event.target.closest?.('#export-adventure')) adventure.exportCurrentAdventure();
    });
    document.addEventListener('htmx:afterSwap', adventure.handleAfterSwap);
    document.addEventListener('htmx:responseError', event => {
        if (!event.detail?.pathInfo?.requestPath?.startsWith('/adventure/')) return;
        adventure.setStatus('Unable to update adventure right now.');
    });
};

adventure.initialize = function () {
    adventure.cacheElements();
    adventure.initializeWorkbenchViews();
    adventure.recentAdapter?.consumeRestore(window.localStorage, window.sessionStorage);
    adventure.bindEvents();
    adventure.renderSelectedSettingDescription();
    dndNextLevel.persistedFragment?.start({
        documentRef: document,
        storage: window.localStorage,
        restoreForm: adventure.restoreForm,
        onStored(saved) {
            if (adventure.stateModule.validate(saved)) {
                adventure.recentAdapter?.saveCurrent(saved, window.localStorage);
            }
        },
    });
};

document.addEventListener('DOMContentLoaded', adventure.initialize);
